/**
 * Chart Colors Configuration
 *
 * Centralized color palettes for chart components.
 * Provides standardized colors for charts based on the number of series needed.
 *
 * Colors are organized by count:
 * - Singles: For charts that only need 1 color
 * - Doubles: For charts that need 2 colors
 * - Triples: For charts that need 3 colors
 *
 * @module chartColors
 */

/**
 * Neutral color for minimal chart mode (no labels, no legend)
 * This color is used when charts should appear non-specific or generic.
 */
export const CHART_NEUTRAL_COLOR = "#DEDFE4";

/**
 * Get the neutral color for minimal chart mode
 *
 * @returns {string} The neutral color hex code
 *
 * @example
 * const color = getNeutralColor(); // '#DEDFE4'
 */
export const getNeutralColor = () => CHART_NEUTRAL_COLOR;

/**
 * Chart color palettes organized by series count
 */
export const CHART_COLORS = {
  singles: [
    ["#349373"], // Single/1
    ["#A4A4A4"], // Single/2
    ["#6B7ED2"], // Single/3
  ],
  doubles: [
    ["#EFCF4E", "#429991"], // Double/1
    ["#B87C4D", "#8570AA"], // Double/2
    ["#BA4D5E", "#404040"], // Double/3
    ["#5F94EF", "#99BF6B"], // Double/4
  ],
  triples: [
    ["#7DBC7D", "#71B0FF", "#485F7C"], // Triple/1
  ],
};

/**
 * Get colors for a single-series chart
 *
 * @param {number} index - Palette index (0-2, defaults to 0)
 * @returns {string[]} Array containing a single color
 *
 * @example
 * const colors = getSingleColors(0); // ['#349373']
 * const colors = getSingleColors(1); // ['#A4A4A4']
 */
export const getSingleColors = (index = 0) => {
  const validIndex = Math.max(0, Math.min(index, CHART_COLORS.singles.length - 1));
  return [...CHART_COLORS.singles[validIndex]];
};

/**
 * Get colors for a two-series chart
 *
 * @param {number} index - Palette index (0-3, defaults to 0)
 * @returns {string[]} Array containing two colors
 *
 * @example
 * const colors = getDoubleColors(0); // ['#EFCF4E', '#429991']
 * const colors = getDoubleColors(1); // ['#B87C4D', '#8570AA']
 */
export const getDoubleColors = (index = 0) => {
  const validIndex = Math.max(0, Math.min(index, CHART_COLORS.doubles.length - 1));
  return [...CHART_COLORS.doubles[validIndex]];
};

/**
 * Get colors for a three-series chart
 *
 * @param {number} index - Palette index (0, defaults to 0)
 * @returns {string[]} Array containing three colors
 *
 * @example
 * const colors = getTripleColors(0); // ['#7DBC7D', '#71B0FF', '#485F7C']
 */
export const getTripleColors = (index = 0) => {
  const validIndex = Math.max(0, Math.min(index, CHART_COLORS.triples.length - 1));
  return [...CHART_COLORS.triples[validIndex]];
};

/**
 * Get colors based on series count
 *
 * @param {number} count - Number of series (1, 2, or 3)
 * @param {number} paletteIndex - Palette index for the given count (defaults to 0)
 * @returns {string[]} Array of colors for the specified count
 *
 * @example
 * const colors = getColorsByCount(1, 0); // ['#349373']
 * const colors = getColorsByCount(2, 1); // ['#B87C4D', '#8570AA']
 * const colors = getColorsByCount(3, 0); // ['#7DBC7D', '#71B0FF', '#485F7C']
 */
export const getColorsByCount = (count, paletteIndex = 0) => {
  if (count === 1) {
    return getSingleColors(paletteIndex);
  }
  if (count === 2) {
    return getDoubleColors(paletteIndex);
  }
  if (count === 3) {
    return getTripleColors(paletteIndex);
  }

  // For counts > 3, return triple colors and repeat as needed
  if (count > 3) {
    const tripleColors = getTripleColors(0);
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(tripleColors[i % tripleColors.length]);
    }
    return result;
  }

  // Default fallback
  return getSingleColors(0);
};

/**
 * Get color for a specific series index
 *
 * @param {number} seriesIndex - Index of the series (0-based)
 * @param {number} count - Total number of series (1, 2, or 3)
 * @param {number} paletteIndex - Palette index for the given count (defaults to 0)
 * @returns {string} Color for the specified series
 *
 * @example
 * const color = getColorForSeries(0, 2, 0); // '#EFCF4E'
 * const color = getColorForSeries(1, 2, 0); // '#429991'
 */
export const getColorForSeries = (seriesIndex, count, paletteIndex = 0) => {
  const colors = getColorsByCount(count, paletteIndex);
  const validIndex = Math.max(0, Math.min(seriesIndex, colors.length - 1));
  return colors[validIndex];
};
